import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import {
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import {
  DataGridPro,
  GridActionsCellItem,
  GridCellParams,
  GridColDef,
  GridRowId,
  GridRowModesModel,
  GridRowParams,
  GridRowSelectionModel,
  GridRowSpacingParams,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { templatesSx } from '@src/components/_shared/sxStyles/Config/configTableList';
import useGetControlModeTemplates from '@src/hooks/api/queries/modes/useGetControlModeTemplates';
import useTemplateMutations from '@src/hooks/api/mutations/useTemplateMutations';
import { ControlModeTemplateSettings } from '@src/types/ControlModes';
import { useEffect, useState } from 'react';
import './Templates.scss';
import CloseIcon from '@mui/icons-material/Close';
import ControlModesTemplateCreateEditModal from './ControlModesTemplateCreateEditModal';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useQueryClient } from '@tanstack/react-query';
import QueryKeys from '@src/constants/queryKeys';
import DeleteTemplateConfirmationDialog from './components/DeleteTemplateConfirmationDialog';
import TemplatesToolbar from './components/TemplatesToolbar';
import ImportResultDialog from './components/ImportResultDialog';
import {
  ImportControlModeTemplateResponseDto,
  TemplateExtended,
  TemplateRow,
  TemplatesListProps,
} from './types';
import { StyledDialogHeader } from './styles';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

export default function TemplatesList({
  selectionMode = false,
  onTemplatesSelected,
  onEditTemplate,
}: TemplatesListProps) {
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<TemplateExtended | null>(null);
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [handleSaveRef, setHandleSaveRef] = useState<
    (() => Promise<void>) | null
  >(null);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<string | undefined>(
    undefined,
  );
  const [importResult, setImportResult] =
    useState<ImportControlModeTemplateResponseDto | null>(null);
  const [importResultDialogOpen, setImportResultDialogOpen] = useState(false);
  const apiRef = useGridApiRef();
  const queryClient = useQueryClient();
  const {
    data: templatesList,
    isFetching,
    isRefetching,
  } = useGetControlModeTemplates();
  const { deleteControlModeTemplate } = useTemplateMutations(templateToDelete);

  // Call onTemplatesSelected when rowSelectionModel changes in selection mode
  useEffect(() => {
    if (selectionMode && onTemplatesSelected && templatesList) {
      const selectedTemplates = templatesList.filter((template) =>
        rowSelectionModel.includes(template.id as string),
      );
      onTemplatesSelected(selectedTemplates);
    }
  }, [selectionMode, onTemplatesSelected, rowSelectionModel, templatesList]);

  const handleEditClick = (id: GridRowId) => {
    if (apiRef.current !== undefined) {
      setSelectedTemplate(apiRef.current.getRow(id));
    }
    setIsCreatingTemplate(false);
    setConfigModalOpen(true);
  };

  const handleCreateClick = () => {
    setSelectedTemplate(null);
    setIsCreatingTemplate(true);
    setConfigModalOpen(true);
  };

  const handleConfigModalClose = () => {
    setConfigModalOpen(false);
    setHandleSaveRef(null);
    setIsCreatingTemplate(false);
  };

  const handleSaveClick = () => {
    if (handleSaveRef) {
      handleSaveRef();
    }
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    setTemplateToDelete(id.toString());
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (templateToDelete) {
      await deleteControlModeTemplate.mutateAsync({});
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.controlModeTemplates],
      });
      setConfirmDialogOpen(false);
      setTemplateToDelete(undefined);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialogOpen(false);
    setTemplateToDelete(undefined);
  };

  const handleImportSuccess = (
    result: ImportControlModeTemplateResponseDto,
  ) => {
    setImportResult(result);
    setImportResultDialogOpen(true);
  };

  const handleImportResultDialogClose = () => {
    setImportResultDialogOpen(false);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      editable: true,
    },
    {
      field: 'settings',
      headerName: 'Controller Fields',
      flex: 1,
      editable: true,
      renderCell: (
        params: GridCellParams<
          TemplateRow,
          string,
          ControlModeTemplateSettings[]
        >,
      ) => {
        const settings: ControlModeTemplateSettings[] = params.row.settings;
        return settings.map((setting) => setting.controllerField).join(', ');
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated At',
      width: 220,
      editable: false,
      renderCell: (
        params: GridCellParams<TemplateRow, Date | undefined, Date | undefined>,
      ) => {
        const updatedAt = params.row.updatedAt;
        if (!updatedAt) {
          return '-';
        }

        return dayjs.utc(updatedAt).local().format('MMM DD, YYYY, hh:mm A');
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      description: 'Actions',
      width: 150,
      cellClassName: 'actions',
      getActions: ({ id }: GridRowParams<TemplateRow>) => {
        // In selection mode with onEditTemplate prop
        if (selectionMode && onEditTemplate) {
          return [
            <GridActionsCellItem
              icon={<EditIcon />}
              label='Edit'
              className='textPrimary'
              onClick={() => {
                // Find the full template in templatesList
                const template = templatesList?.find((t) => t.id === id);
                if (template) {
                  onEditTemplate(template);
                }
              }}
              color='inherit'
              key={`edit-icon-cell-${id}`}
            />,
          ];
        }

        // Normal mode (not selection mode)
        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label='Edit'
            className='textPrimary'
            onClick={() => {
              handleEditClick(id);
            }}
            color='inherit'
            key={`edit-icon-cell-${id}`}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label='Delete'
            onClick={handleDeleteClick(id)}
            color='inherit'
            key={`delete-icon-cell-${id}`}
          />,
        ];
      },
    },
  ];

  const rows: TemplateRow[] =
    templatesList?.map((template) => ({
      id: template.id as string,
      name: template.name,
      settings: template.settings ?? [],
      updatedAt: template.updatedAt,
    })) || [];

  return (
    <main className={selectionMode ? '' : 'mode-templates__main'}>
      <section className={selectionMode ? '' : 'mode-templates__wrapper'}>
        <DataGridPro
          getRowClassName={() => 'MuiDataGrid-clickable templates-row'}
          rows={rows}
          hideFooter
          columns={columns}
          apiRef={apiRef}
          rowModesModel={rowModesModel}
          onRowModesModelChange={setRowModesModel}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={setRowSelectionModel}
          checkboxSelection
          loading={isRefetching || isFetching}
          getRowId={(row) => row.id}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25, page: 0 },
            },
            density: 'comfortable',
            sorting: {
              sortModel: [{ field: 'name', sort: 'asc' }],
            },
          }}
          slots={selectionMode ? {} : { toolbar: TemplatesToolbar }}
          slotProps={{
            loadingOverlay: {
              variant: 'skeleton',
              noRowsVariant: 'skeleton',
            },
            ...(selectionMode
              ? {}
              : {
                  toolbar: {
                    handleAdd: handleCreateClick,
                    onImportSuccess: handleImportSuccess,
                  },
                }),
          }}
          getRowSpacing={(_params: GridRowSpacingParams) => ({
            top: 9,
            bottom: 4,
          })}
          sx={templatesSx.wrapper}
        />

        {!selectionMode && (
          <Dialog
            open={configModalOpen}
            onClose={handleConfigModalClose}
            fullWidth
            maxWidth='md'
          >
            <StyledDialogHeader>
              <Typography
                variant='h6'
                sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}
              >
                {isCreatingTemplate
                  ? 'Create Template'
                  : `Edit Template: ${selectedTemplate?.name}`}
              </Typography>
              <IconButton
                onClick={handleConfigModalClose}
                sx={{ mr: -2, mt: -1 }}
              >
                <CloseIcon />
              </IconButton>
            </StyledDialogHeader>
            <DialogContent dividers>
              {isCreatingTemplate ? (
                <ControlModesTemplateCreateEditModal
                  onClose={handleConfigModalClose}
                  onSave={(
                    saveFunction: () => Promise<void>,
                    saving: boolean,
                  ) => {
                    setHandleSaveRef(() => saveFunction);
                    setIsSaving(saving);
                  }}
                />
              ) : (
                selectedTemplate && (
                  <ControlModesTemplateCreateEditModal
                    templateId={String(selectedTemplate.id)}
                    templateName={selectedTemplate.name}
                    onClose={handleConfigModalClose}
                    onSave={(
                      saveFunction: () => Promise<void>,
                      saving: boolean,
                    ) => {
                      setHandleSaveRef(() => saveFunction);
                      setIsSaving(saving);
                    }}
                  />
                )
              )}
            </DialogContent>
            <DialogActions>
              <MuiButton
                variant='contained'
                color='primary'
                onClick={handleSaveClick}
                disabled={isSaving}
              >
                {'SAVE'}
              </MuiButton>
            </DialogActions>
          </Dialog>
        )}

        <DeleteTemplateConfirmationDialog
          open={confirmDialogOpen}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />

        <ImportResultDialog
          open={importResultDialogOpen}
          onClose={handleImportResultDialogClose}
          result={importResult}
        />
      </section>
    </main>
  );
}
